import React, { useCallback, useState } from 'react';
import { SOCIAL_POST_STATUS_DRAFT, SocialMediaContent, SocialMediaPost } from '../../types';
import {
  FacebookPageDto,
  InstagramAccountDto,
  SOCIAL_MEDIA_ASSET_DIRECT_INSTAGRAM_ACCOUNT,
  SOCIAL_MEDIA_ASSET_FACEBOOK_PAGE,
  SOCIAL_MEDIA_ASSET_INSTAGRAM_ACCOUNT,
  SOCIAL_MEDIA_ASSET_YOUTUBE_CHANNEL,
} from '../../../social-posting/socialPostingApi';
import { useFetchedFacebookAccount } from '../../../facebook/hooks/useFetchedFacebookAccount';
import { SocialMediaAccountPost } from './SocialMediaAccountPost';
import { useMemoCompare } from '../../../hooks/useMemoCompare';
import _ from 'lodash';
import { Button } from 'react-bootstrap';
import { useFetchedInstagramAccount } from '../../../instagram/hooks/useFetchedInstagramAccount';
import { DirectInstagramAccount } from '../../../instagram/types';
import { ConfirmModal } from '../../../commons/components/ConfirmModal';
import { SocialMediaIntegrations } from '../../../social-posting/components/SocialMediaIntegrations';
import { useFetchedYouTubeAccount } from '../../../youtube/hooks/useFetchedYouTubeAccount';
import { YouTubeChannel } from '../../../youtube/types';

export const SocialMediaAccountList = (props: {
  posts: SocialMediaPost[];
  content: SocialMediaContent;
  publishOn: Date;
  onChanged: (posts: SocialMediaPost[]) => void;
}) => {
  const { posts, content, publishOn, onChanged } = props;

  const {
    query: {
      data: facebookAccount,
      isLoading: isFacebookAccountLoading,
      isError: isFacebookAccountLoadingError,
    },
    queryStatusComponent: facebookStatusComponent,
  } = useFetchedFacebookAccount();

  const {
    query: {
      data: instagramAccount,
      isLoading: isInstagramAccountLoading,
      isError: isInstagramAccountLoadingError,
    },
    queryStatusComponent: instagramStatusComponent,
  } = useFetchedInstagramAccount();

  const {
    query: {
      data: youTubeAccount,
      isLoading: isYouTubeAccountLoading,
      isError: isYouTubeAccountLoadingError,
    },
    queryStatusComponent: youTubeStatusComponent,
  } = useFetchedYouTubeAccount();

  const [isIntegrationsModalOpen, setIsIntegrationsModalOpen] = useState(false);

  const isFacebookConnected = !!facebookAccount && !facebookAccount.isDisconnected;
  const isInstagramConnected = !!instagramAccount && !instagramAccount.isDisconnected;
  const isYouTubeConnected = !!youTubeAccount && !youTubeAccount.isDisconnected;

  const primaryButtonLabel =
    isFacebookConnected || isInstagramConnected || isYouTubeConnected
      ? 'Manage'
      : 'Connect Social Accounts';

  const getMatchedAccounts = () => {
    const matchedAccounts: Array<{
      post: SocialMediaPost | null;
      account:
        | FacebookPageDto
        | InstagramAccountDto
        | DirectInstagramAccount
        | YouTubeChannel
        | null;
    }> = [];

    if (isFacebookConnected) {
      facebookAccount.facebookPages.forEach((page) => {
        const post = posts.find(
          (post) => post.type === SOCIAL_MEDIA_ASSET_FACEBOOK_PAGE && post.socialAssetId === page.id
        );
        matchedAccounts.push({
          post: post ? post : null,
          account: page,
        });
      });

      facebookAccount.instagramAccounts.forEach((account) => {
        const post = posts.find(
          (post) =>
            post.type === SOCIAL_MEDIA_ASSET_INSTAGRAM_ACCOUNT && post.socialAssetId === account.id
        );
        matchedAccounts.push({
          post: post ? post : null,
          account: account,
        });
      });
    }

    if (isInstagramConnected) {
      const directInstagramPost = posts.find(
        (post) =>
          post.type === SOCIAL_MEDIA_ASSET_DIRECT_INSTAGRAM_ACCOUNT &&
          post.socialAssetId === instagramAccount.id
      );
      matchedAccounts.push({
        post: directInstagramPost ? directInstagramPost : null,
        account: instagramAccount,
      });
    }

    if (isYouTubeConnected) {
      youTubeAccount.channels.forEach((channel) => {
        const youTubePost = posts.find(
          (post) =>
            post.type === SOCIAL_MEDIA_ASSET_YOUTUBE_CHANNEL && post.socialAssetId === channel.id
        );
        matchedAccounts.push({
          post: youTubePost ? youTubePost : null,
          account: channel,
        });
      });
    }

    posts.forEach((post) => {
      const pageFound =
        post.type === SOCIAL_MEDIA_ASSET_FACEBOOK_PAGE
          ? isFacebookConnected &&
            facebookAccount.facebookPages.some((page) => page.id === post.socialAssetId)
          : post.type === SOCIAL_MEDIA_ASSET_INSTAGRAM_ACCOUNT
          ? isFacebookConnected &&
            facebookAccount.instagramAccounts.some((acc) => acc.id === post.socialAssetId)
          : post.type === SOCIAL_MEDIA_ASSET_DIRECT_INSTAGRAM_ACCOUNT
          ? instagramAccount?.id === post.socialAssetId
          : isYouTubeConnected &&
            youTubeAccount?.channels.some((channel) => channel.id === post.socialAssetId);
      if (!pageFound) {
        matchedAccounts.push({
          post,
          account: null,
        });
      }
    });

    return matchedAccounts;
  };

  const matchedAccounts = useMemoCompare(getMatchedAccounts(), (a, b) => _.isEqual(a, b));

  const handleSelectionChange = useCallback(
    (
      isSelected: boolean,
      account: FacebookPageDto | InstagramAccountDto | DirectInstagramAccount | YouTubeChannel
    ) => {
      const newPosts = [...posts];

      if (isSelected && account.id) {
        const newPost: SocialMediaPost = {
          socialAssetId: account.id,
          status: SOCIAL_POST_STATUS_DRAFT,
          publishing: null,
          type: account.type,
        };
        newPosts.push(newPost);
      } else {
        const index = newPosts.findIndex(
          (post) => post.type === account.type && post.socialAssetId === account.id
        );
        if (index !== -1) {
          newPosts.splice(index, 1);
        }
      }

      onChanged(newPosts);
    },
    [onChanged, posts]
  );

  const handleManageClick = useCallback(() => {
    setIsIntegrationsModalOpen(true);
  }, []);

  const handleManageKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleManageClick();
      }
    },
    [handleManageClick]
  );

  const handleModalClose = useCallback(() => {
    setIsIntegrationsModalOpen(false);
  }, []);

  if (isFacebookAccountLoading || isInstagramAccountLoading || isYouTubeAccountLoading) {
    return <div>Loading social media accounts...</div>;
  }

  return (
    <div className="social-media-account-list">
      <div className="social-media-accounts-horizontal">
        {matchedAccounts.map(({ post, account }, index) => (
          <SocialMediaAccountPost
            key={`${account?.type ? account.type : post?.type}-${account?.id}-${index}`}
            post={post}
            account={account}
            content={content}
            publishOn={publishOn}
            readonly={false}
            onSelectionChange={(isSelected) => {
              if (account) handleSelectionChange(isSelected, account);
            }}
          />
        ))}
        <Button
          variant="primary"
          className="button-apply"
          aria-label={primaryButtonLabel}
          tabIndex={0}
          onClick={handleManageClick}
          onKeyDown={handleManageKeyDown}
        >
          {primaryButtonLabel}
        </Button>
      </div>
      <div className="social-media-account-list__actions">
        {isFacebookAccountLoadingError && facebookStatusComponent}
        {isInstagramAccountLoadingError && instagramStatusComponent}
        {isYouTubeAccountLoadingError && youTubeStatusComponent}
      </div>
      {isIntegrationsModalOpen && (
        <ConfirmModal
          title="Social Media Integrations"
          footer={{
            type: 'show',
            cancelButton: {
              label: 'Close',
              onClick: handleModalClose,
            },
          }}
          onClose={handleModalClose}
          shouldCloseOnOverlayClick
          shouldCloseOnEsc
        >
          <SocialMediaIntegrations />
        </ConfirmModal>
      )}
    </div>
  );
};
